import { Callback1, SingleIndex } from '@deqo/foundation';
import seedrandom from 'seedrandom';

export type RecursiveArray<T> = (T | RecursiveArray<T>)[];
// interface ListOfRecursiveArraysOrValues<T> extends List<T | RecursiveArray<T>> { }

export const Arr = Object.freeze({
    empty: Object.freeze([]),
    all<T>(arr: readonly T[], callback: (x: T, i: number) => boolean): boolean {
        return !arr.find((x, i) => !callback(x, i));
    },
    at<T>(arr: readonly T[], index: number): T | undefined {
        if (arr.length === 0) return undefined;
        while (index < 0) index += arr.length;
        return arr[index % arr.length];
    },
    first<T>(arr: readonly T[]): T | undefined {
        return arr[0];
    },
    last<T>(arr: readonly T[]): T | undefined {
        return arr[arr.length - 1];
    },
    findLowest<T>(arr: readonly T[], callback: (x: T, i: number) => number): T | null {
        let n = Number.MAX_SAFE_INTEGER;
        let e = null as T;
        for (let i = 0; i < arr.length; i++) {
            const v = callback(arr[i], i);
            if (v >= n) continue;
            n = v;
            e = arr[i];
        }
        return e;
    },
    for<T>(arr: readonly T[], callback: (x: T, i: number) => void): void {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < arr.length; i++) {
            callback(arr[i], i);
        }
    },
    rfor<T>(arr: readonly T[], callback: (x: T, i: number) => void): void {
        for (let i = arr.length - 1; i >= 0; i--) {
            callback(arr[i], i);
        }
    },
    replace<T>(arr: T[], newItems: Iterable<T>) {
        arr.splice(0, arr.length, ...Array.from(newItems));
    },
    clear<T>(arr: T[]) {
        arr.splice(0, arr.length);
    },
    append<T>(arr: T[], items: Iterable<T>) {
        for (const tm of items) arr.push(tm);
    },
    addIfNotMatch<T>(arr: T[], tm: T, predicate: (x: T) => boolean) {
        if (!!arr.find(x => predicate(x))) return false;
        arr.push(tm);
        return true;
    },
    remove<T>(arr: T[], tm: T) {
        const i = arr.indexOf(tm);
        if (i < 0) return false;
        arr.splice(i, 1);
        return true;
    },
    removeWhere<T>(arr: T[], predicate: Callback1<boolean, T>) {
        for (let i = 0; i < arr.length; i++) {
            if (predicate(arr[i])) {
                arr.splice(i, 1);
                i--;
            }
        }
    },
    split<T>(arr: readonly T[], primaryPredicate: Callback1<boolean, T>): [T[], T[]] {
        const p = [] as T[];
        const s = [] as T[];
        for (const x of arr) {
            (primaryPredicate(x) ? p : s).push(x);
        }
        return [p, s];
    },
    flatten<T>(arr: RecursiveArray<T>, result: T[] = []) {
        for (let i = 0, length = arr.length; i < length; i++) {
            const value = arr[i];
            if (Array.isArray(value)) {
                Arr.flatten(value, result);
            }
            else {
                result.push(value);
            }
        }
        return result;
    },
    keyBy<T, K extends keyof T>(arr: T[], key: K): Record<string, T> {
        return arr.reduce((t, x) => {
            t[x[key]] = x;
            return t;
        }, {} as any);
    },
    groupBy<T, K extends keyof T>(arr: T[], key: K): Record<string, T[]> {
        return arr.reduce((t, x) => {
            (t[x[key]] = t[x[key]] || []).push(x);
            return t;
        }, {} as any);
    },
    sortBy<T, K extends keyof T>(arr: T[], key: K, dir: 'asc' | 'desc' = 'asc'): T[] {
        return arr.slice().sort((a, b) => {
            const x = a[key];
            const y = b[key];
            let r = 0;
            if (x < y) r = -1;
            if (x > y) r = 1;
            return dir === 'asc' ? r : r * -1;
        });
    },
    equals<T>(a: readonly T[], b: readonly T[]) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    },
    random<T>(a: readonly T[], seed?: string) {
        const rand = !!seed ? seedrandom(seed) : Math.random;
        return a[Math.floor(rand() * a.length)];
    },
    move<T>(arr: T[], from: number, to: number) {
        const startIndex = from < 0 ? arr.length + from : from;
        if (startIndex >= 0 && startIndex < arr.length) {
            const endIndex = to < 0 ? arr.length + to : to;
            const [item] = arr.splice(from, 1);
            arr.splice(endIndex, 0, item);
        }
    },
    max<T>(arr: T[], comp: (a: T, b: T) => number) {
        if (!arr.length) return null;
        let m = arr[0];
        for (const x of arr) {
            if (comp(m, x) > 0) m = x;
        }
        return m;
    },
    avg(arr: number[]) {
        return arr.reduce((p, c) => p + c, 0) / arr.length;
    },
    mode<T>(arr: T[]) {
        const map = new Map<T, number>();
        let max = 0;
        let mode = null;
        for (const x of arr) {
            const count = map.get(x) || 0;
            map.set(x, count + 1);
            if (count > max) {
                max = count;
                mode = x;
            }
        }
        return mode;
    },
    unique<T>(arr: T[], key?: (x: T) => any): T[] {
        key = key || (x => x);
        const res = [] as T[];
        const set = new Set();
        for (const x of arr) {
            const k = key(x);
            if (!set.has(k)) {
                set.add(k);
                res.push(x);
            }
        }
        return res;
    },
});

export const Obj = Object.freeze({
    pick<T extends unknown, K extends keyof T>(base: T, ...keys: K[]): Pick<T, K> {
        const entries = keys.map(key => ([key, base[key]]));
        return Object.fromEntries(entries);
    },
    rename<T extends unknown, K extends keyof T, N extends string>(base: T, key: K, to: N): Omit<T, K> & { [P in N]: T[K] } {
        const result: any = Object.assign(
            {},
            { [to]: base[key] },
            Object.fromEntries(Object.entries(base).filter(x => x[0] !== key)),
        );
        return result;
    },
    sortKeys<K extends string, V>(obj: Record<K, V>): Record<K, V> {
        return Object.fromEntries(Object.entries(obj).sort(([a], [b]) => a.localeCompare(b))) as any;
    },
});

export class DoubleMap<UK, LK, T> implements Iterable<[UK, ReadonlyMap<LK, T>]> {

    private store = new Map<UK, Map<LK, T>>();

    public clear(upperKey?: UK): void {
        if (upperKey) {
            this.store.get(upperKey)?.clear();
        }
        else {
            this.store.clear();
        }
    }

    public delete(upperKey: UK, lowerKey: LK): void {
        this.store.get(upperKey)?.delete(lowerKey);
    }

    public get(upperKey: UK, lowerKey: LK): T {
        return this.store.get(upperKey)?.get(lowerKey);
    }

    public has(upperKey: UK, lowerKey?: LK): boolean {
        const lm = this.store.get(upperKey);
        if (lm) {
            return (lowerKey === undefined) ? true : lm.has(lowerKey);
        }
        return false;
    }

    public set(upperKey: UK, lowerKey: LK, value: T): this {
        const lm = this.store.get(upperKey) ?? this.allocate(upperKey);
        lm.set(lowerKey, value);
        return this;
    }

    public keys(): IterableIterator<UK>;
    public keys(upperKey: UK): IterableIterator<LK>;
    public keys(upperKey?: UK): IterableIterator<UK | LK> {
        return upperKey === undefined ? this.store.keys() : this.store.get(upperKey)?.keys() ?? [][Symbol.iterator]();
    }

    public values(): IterableIterator<ReadonlyMap<LK, T>>;
    public values(upperKey: UK): IterableIterator<T>;
    public values(upperKey?: UK): IterableIterator<ReadonlyMap<LK, T> | T> {
        return upperKey === undefined ? this.store.values() : this.store.get(upperKey)?.values() ?? [][Symbol.iterator]();
    }

    private allocate(upperKey: UK) {
        const lm = new Map<LK, T>();
        this.store.set(upperKey, lm);
        return lm;
    }

    [Symbol.iterator](): Iterator<[UK, ReadonlyMap<LK, T>]> {
        return this.store[Symbol.iterator]();
    }
}

export const Maps = Object.freeze({

    merge<K, V>(...maps: Map<K, V>[]) {
        const merge = new Map<K, V>();
        for (const m of maps) {
            for (const k of m.keys()) {
                merge.set(k, m.get(k));
            }
        }
        return merge;
    }

});

// https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
export function shuffle<T>(array: T[], random?: () => number): T[] {
    random = random || (() => Math.random());
    let currentIndex = array.length;
    let randomIndex = 0;

    // While there remain elements to shuffle...
    while (currentIndex !== 0) {

        // Pick a remaining element...
        randomIndex = Math.floor(random() * currentIndex);
        currentIndex--;

        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex], array[currentIndex]];
    }

    return array;
}

/* eslint-disable */
// Adapted from:
// https://raw.githubusercontent.com/alexreardon/memoize-one/master/src/memoize-one.ts

const MemoStore = Symbol('MemoStore');

const isEqual = ((a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
});

const createMemoized = <T extends (this: any, ...newArgs: any[]) => ReturnType<T>>(target: T, name: string | symbol, maxCache: number) => {
    return function (this: any, ...newArgs: unknown[]): ReturnType<T> {

        const store = this[MemoStore] ?? (this[MemoStore] = new Map<string, any>());
        const key = `${name.toString()}:${JSON.stringify(newArgs)})}`;

        if (store.has(key)) {
            return store.get(key);
        }

        if (store.size >= maxCache) {
            console.error(`MemoStore is over ${maxCache} on ${name.toString()}!  This method should not be annotated with memoAll().`);
        }

        const newResult = target.apply(this, newArgs);
        store.set(key, newResult);
        return newResult;
    };
};

export const memoAll = (maxCache = 25): MethodDecorator =>
    (_target: object, properyKey: string | symbol, descriptor: PropertyDescriptor): PropertyDescriptor => {
        descriptor.value = createMemoized(descriptor.value, properyKey, maxCache);
        return descriptor;
    };

export const indexBy = <T, K>(arr: T[], keyFn: (x: T) => K): SingleIndex<K, T> => {
    const index = new SingleIndex<K, T>();
    for (const x of arr) {
        const k = keyFn(x);
        index.add(k, x);
    }
    return index;
};